/**
 * Wire types for 重点保障中心 (#2583, modes #2602).
 *
 * SKU ultimate fallback is `off|shadow|act`. Org×SKU overlays are
 * `inherit|off|shadow|act`. The write face still accepts legacy `on`
 * and stores it as `act`. Resolution: org overlay if not inherit, else
 * the SKU row, else off. The request path walks only for `act`.
 */

export const GUARANTEED_SCOPES = ['sku', 'org_sku'] as const;
export type GuaranteedScope = (typeof GUARANTEED_SCOPES)[number];

export const GUARANTEED_MODES = ['off', 'shadow', 'act'] as const;
export type GuaranteedMode = (typeof GUARANTEED_MODES)[number];

/** Write-face values. `on` is accepted for one release and stored as `act`. */
export const GUARANTEED_SKU_SWITCHES = ['off', 'shadow', 'act', 'on'] as const;
export type GuaranteedSkuSwitch = (typeof GUARANTEED_SKU_SWITCHES)[number];

export const GUARANTEED_JEV_MODES = ['off', 'shadow', 'act'] as const;
export type GuaranteedJevMode = (typeof GUARANTEED_JEV_MODES)[number];

/** Write-face overlay values. `on` is accepted and stored as `act`. */
export const GUARANTEED_OVERRIDES = ['inherit', 'off', 'shadow', 'act', 'on'] as const;
export type GuaranteedOverride = (typeof GUARANTEED_OVERRIDES)[number];

export function isGuaranteedScope(value: unknown): value is GuaranteedScope {
  return value === 'sku' || value === 'org_sku';
}

export function isGuaranteedMode(value: unknown): value is GuaranteedMode {
  return value === 'off' || value === 'shadow' || value === 'act';
}

export function isGuaranteedSkuSwitch(value: unknown): value is GuaranteedSkuSwitch {
  return value === 'off' || value === 'shadow' || value === 'act' || value === 'on';
}

/** `on` → `act`. Anything else that is not a mode returns null. */
export function canonicalGuaranteedMode(value: unknown): GuaranteedMode | null {
  if (value === 'on') return 'act';
  return isGuaranteedMode(value) ? value : null;
}

/** Jev trip criteria bounds (per SKU, #2602): N qualifying verdicts inside W seconds. */
export const GUARANTEED_JEV_TRIP_MIN_HITS_DEFAULT = 5;
export const GUARANTEED_JEV_TRIP_MIN_HITS_MIN = 1;
export const GUARANTEED_JEV_TRIP_MIN_HITS_MAX = 100;
export const GUARANTEED_JEV_TRIP_WINDOW_SECONDS_DEFAULT = 300;
export const GUARANTEED_JEV_TRIP_WINDOW_SECONDS_MIN = 10;
export const GUARANTEED_JEV_TRIP_WINDOW_SECONDS_MAX = 3600;

/** Probe round criteria bounds (per SKU, user 2026-09-22). */
export const GUARANTEED_PROBE_SHOTS_PER_ROUND_DEFAULT = 2;
export const GUARANTEED_PROBE_SHOTS_PER_ROUND_MAX = 5;
export const GUARANTEED_PROBE_TRIP_FAILED_ROUNDS_DEFAULT = 1;
export const GUARANTEED_PROBE_TRIP_FAILED_ROUNDS_MAX = 10;
export const GUARANTEED_PROBE_RECOVER_OK_ROUNDS_DEFAULT = 2;
export const GUARANTEED_PROBE_RECOVER_OK_ROUNDS_MAX = 10;

function isIntIn(value: unknown, min: number, max: number): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= min && value <= max;
}

export function isGuaranteedProbeShotsPerRound(value: unknown): value is number {
  return isIntIn(value, 1, GUARANTEED_PROBE_SHOTS_PER_ROUND_MAX);
}

export function isGuaranteedProbeTripFailedRounds(value: unknown): value is number {
  return isIntIn(value, 1, GUARANTEED_PROBE_TRIP_FAILED_ROUNDS_MAX);
}

export function isGuaranteedProbeRecoverOkRounds(value: unknown): value is number {
  return isIntIn(value, 1, GUARANTEED_PROBE_RECOVER_OK_ROUNDS_MAX);
}

export function isGuaranteedJevTripMinHits(value: unknown): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value)
    && value >= GUARANTEED_JEV_TRIP_MIN_HITS_MIN && value <= GUARANTEED_JEV_TRIP_MIN_HITS_MAX;
}

export function isGuaranteedJevTripWindowSeconds(value: unknown): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value)
    && value >= GUARANTEED_JEV_TRIP_WINDOW_SECONDS_MIN && value <= GUARANTEED_JEV_TRIP_WINDOW_SECONDS_MAX;
}

export function isGuaranteedJevMode(value: unknown): value is GuaranteedJevMode {
  return value === 'off' || value === 'shadow' || value === 'act';
}

export const GUARANTEED_PROBE_SHAPES = ['ping', 'arithmetic', 'echo', 'custom'] as const;
export type GuaranteedProbeShape = (typeof GUARANTEED_PROBE_SHAPES)[number];

export function isGuaranteedProbeShape(value: unknown): value is GuaranteedProbeShape {
  return value === 'ping' || value === 'arithmetic' || value === 'echo' || value === 'custom';
}

export function isGuaranteedOverride(value: unknown): value is GuaranteedOverride {
  return value === 'inherit' || value === 'off' || value === 'shadow' || value === 'act' || value === 'on';
}

/** Stored overlay value. Legacy `on` becomes `act`. */
export function canonicalGuaranteedOverride(value: unknown): 'inherit' | GuaranteedMode | null {
  if (value === 'inherit') return 'inherit';
  return canonicalGuaranteedMode(value);
}

/** Resolved values consumed by the gateway and by #2585. Overlay jev is ignored. */
export interface GuaranteedServiceResolved {
  readonly jev_judge: GuaranteedJevMode;
  /** True only when the effective mode is `act` (legacy `on` included). */
  readonly ultimate_fallback: boolean;
  readonly ultimate_fallback_mode: GuaranteedMode;
}

export interface GuaranteedServiceSkuEntry {
  readonly id: string;
  readonly scope: 'sku';
  readonly org_id: null;
  readonly sku_id: string;
  readonly sku_name: string;
  readonly jev_judge: GuaranteedJevMode;
  readonly ultimate_fallback: GuaranteedMode;
  readonly probe_mode: GuaranteedMode;
  readonly probe_enabled: boolean;
  /** Minutes between probe rounds. Integer 1–1440; default 30. */
  readonly probe_interval_minutes: number;
  /** Non-stream user-message probe. Default `ping`. Meaningful on SKU rows. */
  readonly probe_shape: GuaranteedProbeShape;
  /** Required when `probe_shape` is `custom`. Null for the other shapes. */
  readonly probe_custom_text: string | null;
  /** 1–64 when set. Null uses 16. Null for non-custom shapes. */
  readonly probe_custom_max_tokens: number | null;
  readonly chain_trip_mode: GuaranteedMode;
  readonly chain_trip: boolean;
  /** Probe round criteria (user 2026-09-22): shots per round (1–5, default 2)… */
  readonly probe_shots_per_round: number;
  /** …consecutive failed rounds that trip (1–10, default 1)… */
  readonly probe_trip_failed_rounds: number;
  /** …consecutive passing rounds that recover (1–10, default 2). */
  readonly probe_recover_ok_rounds: number;
  /** Jev trips after this many qualifying verdicts on one leg… (1–100, default 5). */
  readonly jev_trip_min_hits: number;
  /** …inside this window (10–3600 s, default 300). Also the verdict dedupe window. */
  readonly jev_trip_window_seconds: number;
  readonly note: string | null;
  readonly created_by: string | null;
  readonly created_at: string;
  readonly updated_at: string;
  readonly request_count_24h: number;
  readonly entered_chain_count_24h: number;
  readonly chain_exhausted_count_24h: number;
  /** `guaranteed_shadow_enter` rows in the last 24 hours. */
  readonly shadow_enter_24h: number;
}

export interface GuaranteedServiceOrgSkuEntry {
  readonly id: string;
  readonly scope: 'org_sku';
  readonly org_id: string;
  readonly sku_id: string;
  readonly sku_name: string;
  readonly jev_judge: GuaranteedOverride;
  readonly ultimate_fallback: 'inherit' | GuaranteedMode;
  readonly effective: GuaranteedServiceResolved;
  readonly note: string | null;
  readonly created_by: string | null;
  readonly created_at: string;
  readonly updated_at: string;
  readonly request_count_24h: number;
  readonly entered_chain_count_24h: number;
  readonly chain_exhausted_count_24h: number;
  readonly shadow_enter_24h: number;
}

export type GuaranteedServiceEntry = GuaranteedServiceSkuEntry | GuaranteedServiceOrgSkuEntry;

export interface ListGuaranteedServiceEntriesResponse {
  readonly sku: readonly GuaranteedServiceSkuEntry[];
  readonly org_sku: readonly GuaranteedServiceOrgSkuEntry[];
}

export interface CreateGuaranteedServiceSkuRequest {
  readonly scope: 'sku';
  readonly sku_ids: readonly string[];
  /** Omitted means `off`. */
  readonly jev_judge?: GuaranteedJevMode;
  /** Omitted means `off`. `on` is stored as `act`. */
  readonly ultimate_fallback?: GuaranteedSkuSwitch;
  readonly probe_mode?: GuaranteedMode;
  readonly probe_enabled?: boolean;
  readonly probe_interval_minutes?: number;
  readonly probe_shape?: GuaranteedProbeShape;
  readonly probe_custom_text?: string | null;
  readonly probe_custom_max_tokens?: number | null;
  readonly chain_trip_mode?: GuaranteedMode;
  readonly chain_trip?: boolean;
  readonly probe_shots_per_round?: number;
  readonly probe_trip_failed_rounds?: number;
  readonly probe_recover_ok_rounds?: number;
  readonly jev_trip_min_hits?: number;
  readonly jev_trip_window_seconds?: number;
  readonly note?: string | null;
}

export interface CreateGuaranteedServiceOrgSkuRequest {
  readonly scope: 'org_sku';
  readonly org_id: string;
  readonly sku_ids: readonly string[];
  /** Omitted means `inherit`. `on` is stored as `act`. */
  readonly ultimate_fallback?: GuaranteedOverride;
  readonly note?: string | null;
}

export type CreateGuaranteedServiceRequest =
  | CreateGuaranteedServiceSkuRequest
  | CreateGuaranteedServiceOrgSkuRequest;

export interface CreateGuaranteedServiceResponse {
  readonly items: readonly GuaranteedServiceEntry[];
}

export interface PatchGuaranteedServiceSkuRequest {
  readonly jev_judge?: GuaranteedJevMode;
  readonly ultimate_fallback?: GuaranteedSkuSwitch;
  readonly probe_mode?: GuaranteedMode;
  readonly probe_enabled?: boolean;
  readonly probe_interval_minutes?: number;
  readonly probe_shape?: GuaranteedProbeShape;
  readonly probe_custom_text?: string | null;
  readonly probe_custom_max_tokens?: number | null;
  readonly chain_trip_mode?: GuaranteedMode;
  readonly chain_trip?: boolean;
  readonly probe_shots_per_round?: number;
  readonly probe_trip_failed_rounds?: number;
  readonly probe_recover_ok_rounds?: number;
  readonly jev_trip_min_hits?: number;
  readonly jev_trip_window_seconds?: number;
  readonly note?: string | null;
}

export interface PatchGuaranteedServiceOrgSkuRequest {
  readonly ultimate_fallback?: GuaranteedOverride;
  readonly note?: string | null;
}

export type PatchGuaranteedServiceRequest =
  | PatchGuaranteedServiceSkuRequest
  | PatchGuaranteedServiceOrgSkuRequest;

export interface PatchGuaranteedServiceResponse {
  readonly item: GuaranteedServiceEntry;
}

export interface DeleteGuaranteedServiceResponse {
  readonly deleted: true;
}

export interface GuaranteedServiceTextSku {
  readonly sku_id: string;
  readonly sku_name: string;
}

export interface ListGuaranteedServiceTextSkusResponse {
  readonly skus: readonly GuaranteedServiceTextSku[];
}

/**
 * Pure two-layer resolution (#2583). Org overlay wins when not `inherit`;
 * otherwise the SKU row; otherwise both switches off.
 *
 * Stable for #2585: that Issue consumes `jev_judge` from this function
 * and must not re-derive the ladder.
 */
export function resolveGuaranteedService(params: {
  readonly sku: {
    readonly jev_judge: GuaranteedJevMode;
    readonly ultimate_fallback: GuaranteedSkuSwitch;
  } | null;
  readonly orgSku: {
    readonly ultimate_fallback: GuaranteedOverride;
  } | null;
}): GuaranteedServiceResolved {
  const chosen = params.orgSku !== null && params.orgSku.ultimate_fallback !== 'inherit'
    ? params.orgSku.ultimate_fallback
    : (params.sku?.ultimate_fallback ?? 'off');
  const mode = canonicalGuaranteedMode(chosen) ?? 'off';
  return {
    jev_judge: params.sku?.jev_judge ?? 'off',
    ultimate_fallback: mode === 'act',
    ultimate_fallback_mode: mode,
  };
}

export const GUARANTEED_EVENT_PAGE_SIZE = 20;
export const GUARANTEED_EVENT_MAX_PAGE_SIZE = 100;

export interface GuaranteedServiceEventRecord {
  readonly event_ts: string;
  readonly kind: string;
  readonly sku_id: string | null;
  readonly upstream_id: string | null;
  readonly upstream_name: string | null;
  readonly account_id: string | null;
  readonly key_bucket_id: string | null;
  readonly upstream_channel_id: string | null;
  readonly org_id: string | null;
  readonly region: string;
  readonly details: Readonly<Record<string, unknown>> | null;
  readonly source: 'load_event' | 'verdict';
}

export interface ListGuaranteedServiceEventsResponse {
  readonly events: readonly GuaranteedServiceEventRecord[];
  readonly total: number;
  readonly page: number;
  readonly page_size: number;
}
